import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HoverPreview } from './HoverPreview';

interface GameCardProps {
  id: string;
  title: string;
  category: string;
  thumbnail: string;
  badge?: string;
}

export const GameCard: React.FC<GameCardProps> = ({ id, title, category, thumbnail, badge }) => {
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(true);
    }, 300); // 300ms delay before loading preview
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(false);
  };

  return (
    <Link 
      to={`/game/${id}`}
      style={{ textDecoration: 'none', color: 'inherit', display: 'block', minWidth: '200px' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        {/* Image Container */}
        <div style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16/9',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#e5e7eb',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          {/* Base Thumbnail */}
          <img 
            src={thumbnail} 
            alt={title} 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              position: 'absolute',
              top: 0, left: 0,
              zIndex: 1,
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
              transition: 'transform 0.4s ease-out'
            }} 
            loading="lazy"
          />
          
          {/* Hover Preview Overlay */}
          {isHovered && (
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2 }}>
              <HoverPreview thumbnailUrl={thumbnail} />
            </div>
          )}

          {/* Badge */}
          {badge && (
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              backgroundColor: '#ef4444',
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              padding: '4px 8px',
              borderRadius: '6px',
              zIndex: 10,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              {badge}
            </div>
          )}
        </div>

        {/* Text Details */}
        <div style={{ padding: '0 4px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '1rem', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            {category}
          </div>
        </div>
      </div>
    </Link>
  );
};
